import { Box, IconButton, useTheme } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useMeeting,usePubSub } from "@videosdk.live/react-sdk";
import React, { useEffect, useMemo, useState, useRef } from "react";
import { useMediaQuery } from "react-responsive";
import { useMeetingAppContext,meetingLayouts } from "../../MeetingAppContextDef";
import {
  calcQuality,
  getGridForMainParticipants,
  getGridRowsAndColumns,
  localAndPinnedOnTop,
  meetingResolutions,
} from "../../utils/common";
import useIsLGDesktop from "../../utils/useIsLGDesktop";
import useIsMobile from "../../utils/useIsMobile";
import useIsSMDesktop from "../../utils/useIsSMDesktop";
import useIsTab from "../../utils/useIsTab";
import useResponsiveSize from "../../utils/useResponsiveSize";
import { MemoizedMotionParticipant } from "../mainViewContainer/MainViewContainer";
import ParticipantViewer from "../mainViewContainer/ParticipantViewer";
import PresenterView from "../mainViewContainer/PresenterView";
import WhiteboardContainer, {
  convertHWAspectRatio,
} from "../../components/whiteboard/WhiteboardContainer";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { 
  calculatePaddingStyles, 
  calculateSidebarOffset, 
  calculateInnerWidth,
  calculateWhiteboardDimensions,
  getQualitySetting,
  calculateSpotlightFlex,
  calculateSidebarFlex
} from "../../utils/layoutCalculations";
import { 
  createBannerUpdateHandlers, 
  createBackgroundUpdateHandlers, 
  createOverlayUpdateHandlers,
  createMusicUpdateHandlers,
  createLogoUpdateHandlers
} from "../../utils/pubSubUtils";
import { calculatePresenterSidebarWidth } from "../../utils/responsiveUtils";
import { getAnimationDuration } from "../../utils/animationUtils";

export const PaddingTop = (meetingOverlay, bannerName, isRecorder) => {
  if (meetingOverlay || bannerName) {
    return isRecorder ? "7%" : "5%";
  }
  return "0%";
};

const PinnedLayoutViewContainer = ({
  height,
  width,
  whiteboardToolbarWidth,
  whiteboardSpacing,
  left
}) => {
  const {
    meetingLayout,
    sideBarMode,
    whiteboardStarted,
    animationsEnabled,
    reduceEdgeSpacing,
    isRecorder,
    layoutGridSize,
    appTheme,
    meetingResolution,
  } = useMeetingAppContext();
  const sideBarData = useSelector((state) => state?.sidebar);
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const position = queryParams.get('position') || 'bl';
  const guest = queryParams.get("guest");
  const color = queryParams.get('color') || 'white';

  const mMeeting = useMeeting();
  const localParticipantId = mMeeting?.localParticipant?.id;
  const presenterId = mMeeting?.presenterId;
  const pinnedParticipants = mMeeting?.pinnedParticipants;
  const transcriptionState = mMeeting?.transcriptionState;

  const isMobile = useIsMobile();
  const isTab = useIsTab();
  const isSMDesktop = useIsSMDesktop();
  const isLGDesktop = useIsLGDesktop();
  const isPortrait = useMediaQuery({ query: "(orientation: portrait)" });
  const mobilePortrait = isMobile && isPortrait;
  const {
    spotlightParticipantId,
    sideBarPinnedParticipantIds,
    spotlightMediaType,
    singleRow,
  } = useMemo(() => {
    let pinnedParticipantIds = [...pinnedParticipants.keys()];

    const pinnedPresenter =
      pinnedParticipantIds.findIndex((id) => id === presenterId) !== -1;

    if (isRecorder && pinnedParticipantIds.length > layoutGridSize) {
      if (pinnedPresenter) {
        const pinnedParticipantIndexToBeRemoved =
          pinnedParticipantIds.findIndex((id) => id === presenterId);

        pinnedParticipantIds.splice(pinnedParticipantIndexToBeRemoved, 1);

        pinnedParticipantIds = [pinnedPresenter, ...pinnedParticipantIds];
      }

      pinnedParticipantIds = pinnedParticipantIds.slice(0, layoutGridSize);
    }

    let obj;

    if (pinnedPresenter) {
      obj = {
        spotlightParticipantId: presenterId,
        sideBarPinnedParticipantIds:
          meetingLayout === meetingLayouts.SPOTLIGHT
            ? []
            : pinnedParticipantIds,
        spotlightMediaType: "SHARE",
      };
    } else if (whiteboardStarted) {
      if (meetingLayout === meetingLayouts.SPOTLIGHT) {
        obj = {
          spotlightParticipantId: "white-board-id",
          sideBarPinnedParticipantIds: [],
          spotlightMediaType: "WHITEBOARD",
        };
      } else {
        obj = {
          spotlightParticipantId: "white-board-id",
          sideBarPinnedParticipantIds: pinnedParticipantIds,
          spotlightMediaType: "WHITEBOARD",
        };
      }
    } else {
      if (meetingLayout === meetingLayouts.SPOTLIGHT) {
        obj = {
          spotlightParticipantId: null,
          sideBarPinnedParticipantIds: pinnedParticipantIds,
          spotlightMediaType: null,
        };
      } else {
        const spotlightParticipantId = pinnedParticipantIds[0];
        pinnedParticipantIds.splice(0, 1);

        obj = {
          spotlightParticipantId: spotlightParticipantId,
          sideBarPinnedParticipantIds: pinnedParticipantIds,
          spotlightMediaType: "WEBCAM",
        };
      }
    }

    if (mobilePortrait && meetingLayout === meetingLayouts.SIDEBAR) {
      const splicesActiveParticipants = obj[
        "sideBarPinnedParticipantIds"
      ].splice(0, 4);

      obj["sideBarPinnedParticipantIds"] = splicesActiveParticipants;
    }

    const participantsCount = obj.sideBarPinnedParticipantIds?.length;

    if (participantsCount) {
      const pinnedParticipantsMap = new Map(pinnedParticipants);

      if (obj["spotlightMediaType"] === "WEBCAM") {
        if (obj["spotlightParticipantId"]) {
          pinnedParticipantsMap.delete(obj["spotlightParticipantId"]);
        }
      }

      const gridInfo = getGridRowsAndColumns({
        participantsCount,
        isMobile:
          mobilePortrait && meetingLayout === meetingLayouts.SIDEBAR
            ? false
            : isMobile,
        isTab:
          mobilePortrait && meetingLayout === meetingLayouts.SIDEBAR
            ? false
            : isTab,
        isSMDesktop:
          mobilePortrait && meetingLayout === meetingLayouts.SIDEBAR
            ? false
            : isSMDesktop,
        isLGDesktop:
          mobilePortrait && meetingLayout === meetingLayouts.SIDEBAR
            ? true
            : isLGDesktop,
        isLandscape:
          mobilePortrait && meetingLayout === meetingLayouts.SIDEBAR
            ? true
            : !isPortrait,
        isPresenting:
          mobilePortrait && meetingLayout === meetingLayouts.SIDEBAR
            ? false
            : !!obj.spotlightParticipantId,
      });

      const { singleRow } = getGridForMainParticipants({
        participants: localAndPinnedOnTop({
          localParticipantId,
          participants: [...obj.sideBarPinnedParticipantIds],
          pinnedParticipantIds: [...pinnedParticipantsMap.keys()],
          moveLocalUnpinnedOnTop: false,
        }),
        gridInfo,
      });

      obj["singleRow"] = singleRow;
    } else {
      obj["singleRow"] = [];
    }

    return obj;
  }, [
    mobilePortrait,
    pinnedParticipants,
    meetingLayout,
    presenterId,
    localParticipantId,
    isMobile,
    isTab,
    isSMDesktop,
    isLGDesktop,
    isPortrait,
    whiteboardStarted,
    isRecorder,
    layoutGridSize,
  ]);

  const theme = useTheme();

  const rowSpacing = useResponsiveSize({
    xl: 24,
    lg: 16,
    md: 14,
    sm: 12,
    xs: 8,
  });

  const gutter = 4;
  const spacing = (reduceEdgeSpacing ? 4 : rowSpacing) - gutter;

  const _presentingSideBarWidth = useResponsiveSize({
    xl: 320,
    lg: 280,
    md: 260,
    sm: 240,
    xs: 200,
  });

  const isVeryLargeScreen = useMediaQuery({ query: "(min-width: 2560px)" });
  const isUltraLargeScreen = useMediaQuery({ query: "(min-width: 3840px)" });

  const presentingSideBarWidthNum = useMemo(() => {
    if ((whiteboardStarted || presenterId) &&
      meetingLayout === meetingLayouts.SPOTLIGHT) {
      return 0;
    }
    return calculatePresenterSidebarWidth(_presentingSideBarWidth, isVeryLargeScreen, isUltraLargeScreen);
  }, [_presentingSideBarWidth, isVeryLargeScreen, isUltraLargeScreen, whiteboardStarted, presenterId, meetingLayout]);

  const presentingSideBarWidth = presentingSideBarWidthNum;

  const _mainContainerHorizontalPadding =
    spotlightParticipantId && singleRow.length !== 0
      ? 0
      : typeof sideBarMode === "string"
        ? 0
        : sideBarPinnedParticipantIds?.length <= 9
          ? isLGDesktop
            ? singleRow.length === 2
              ? 0
              : 140
            : isSMDesktop
              ? singleRow.length === 2
                ? 20
                : 90
              : isTab && !isPortrait
                ? 60
                : 0
          : 0;

  const mainContainerHorizontalPadding = useMemo(() => {
    return reduceEdgeSpacing
      ? 0
      : (whiteboardStarted || presenterId) &&
        meetingLayout === meetingLayouts.SPOTLIGHT
        ? 0
        : _mainContainerHorizontalPadding;
  }, [
    _mainContainerHorizontalPadding,
    whiteboardStarted,
    presenterId,
    meetingLayout,
    reduceEdgeSpacing,
  ]);

  const gridVerticalSpacing = useResponsiveSize({
    xl: 160,
    lg: 90,
    md: 90,
    sm: 60,
    xs: 60,
  });


  const [bannerName, setBannerName] = useState('');
  const [meetingBackground, setMeetingBackground] = useState("");
  const [meetingOverlay, setMeetingOverlay] = useState("");
  const [logoPosition, setLogoPosition] = useState({ x: 0, y: 0 });
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  const bannerType = [
    {
      // width: isVisible ? '75%' : '0px',
      transform: isVisible ? 'translateX(0)' : 'translateX(-100%)',
      borderRadius: '0.5vh',
    },
    {
      marginLeft: '1vh',
    }, {
      marginLeft: '1vh',
      borderRadius: '100px',
    }, {
      width: '75%',
      clipPath: isVisible
        ? 'polygon(0% 100%, 98% 100%, 100% 0%, 0% 0%)'
        : 'polygon(0% 100%, 0% 100%, 0% 0%, 0% 0%)',
      borderTopRightRadius: '1vh',
    }
  ]


  usePubSub("UPDATE_BANNER", createBannerUpdateHandlers(setBannerName));

  usePubSub("UPDATE_LOGO", createLogoUpdateHandlers(setLogoPosition));

  usePubSub("UPDATE_MEETING_BACKGROUND", createBackgroundUpdateHandlers(setMeetingBackground));

  usePubSub("UPDATE_MEETING_OVERLAY", createOverlayUpdateHandlers(setMeetingOverlay));

  const audioRef = useRef(null);
  usePubSub("UPDATE_MUSIC", createMusicUpdateHandlers(audioRef));

  const { paddingLeft, paddingRight } = calculatePaddingStyles(meetingOverlay, bannerName);

  // Get sidebar width from localStorage or default to 80
  const sidebarOffset = calculateSidebarOffset(guest);

  // Calculate flex values
  const spotlightContainerFlex = calculateSpotlightFlex(spotlightParticipantId, sideBarPinnedParticipantIds);
  const sidebarContainerFlex = calculateSidebarFlex(spotlightParticipantId, sideBarPinnedParticipantIds, presentingSideBarWidth);

  const innerWidth = calculateInnerWidth({
    width,
    spacing,
    mobilePortrait,
    spotlightParticipantId,
    sideBarPinnedParticipantIds,
    presentingSideBarWidth,
    sidebarOffset,
    singleRow,
    mainContainerHorizontalPadding,
    sideBarType: sideBarData.type
  });

  const whiteboardDims = calculateWhiteboardDimensions({
    width,
    height,
    spacing,
    isMobile,
    presentingSideBarWidth,
    whiteboardToolbarWidth,
    whiteboardSpacing,
    whiteboardStarted,
    convertHWAspectRatio
  });

  const renderSpotlightContent = () => {
    if (!spotlightParticipantId) return null;

    if (spotlightMediaType === "SHARE") {
      return (
        <PresenterView
          key={`spotlightParticipantId_${spotlightParticipantId}`}
          presenterId={spotlightParticipantId}
        />
      );
    }

    if (spotlightMediaType === "WHITEBOARD") {
      return (
        <WhiteboardContainer {...whiteboardDims} />
      );
    }

    return (
      <div
        style={{
          position: "relative",
          transition: `height ${getAnimationDuration(animationsEnabled)}ms`,
          transitionTimingFunction: "ease-in-out",
          height: '100%',
          width: meetingLayout === 'UNPINNED_SIDEBAR' && singleRow.length > 0 ? '80%' : '100%',
        }}
      >
        <MemoizedMotionParticipant
          participantId={spotlightParticipantId}
          gutter={gutter}
          quality={getQualitySetting(meetingResolution, meetingResolutions)}
          relativeHeight={100}
          relativeWidth={100}
          relativeTop={0}
          relativeLeft={0}
          key={`spotlightParticipantId_${spotlightParticipantId}`}
        />
      </div>
    );
  };


  return (
    <>
      <div
        style={{
          position: 'absolute', // Absolutely position the background image
          top: '0',
          left: '0',
          // right: guest? '0px': '80px',
          width: '100%',
          height: '100%', // Ensure the div fills its container
          backgroundImage: `url(${meetingOverlay})`,
          backgroundSize: '100% 100%', // Ensures it covers the entire div
          backgroundPosition: 'center', // Centers the image
          backgroundRepeat: 'no-repeat', // Prevents tiling
          zIndex: 1000, // Ensure it's below other elements
        }}
      ></div>
      <div style={{
        height: '100%',
        width: '100%',
        display: "flex", // Add this to create a row layout
        alignItems: "center",
        justifyContent: "center",
        flexDirection: "row", // Make sure items align horizontally
        paddingTop: 0,
        paddingBottom: 0,
        paddingLeft,
        paddingRight,
      }}>
        {mMeeting?.pinnedParticipants.size > 0 && (
          <>
            <div
              style={{
                width: spotlightParticipantId
                  ? sideBarPinnedParticipantIds?.length > 0
                    ? undefined
                    : '100%'
                  : 0,
                flex: spotlightContainerFlex,
                // width: '100%',
                // height: mobilePortrait && !presenterId ? height / 2 : height,
                height: '100%',
                transition: `flex ${800 * (animationsEnabled ? 1 : 0.5)}ms`,
                transitionTimingFunction: "ease-in-out",
                // paddingTop:spacing,
                // paddingLeft: spacing,
                paddingLeft: spotlightMediaType === "SHARE"
                  ? spacing
                  : 0,
                paddingTop: spotlightMediaType === "SHARE"
                  ? spacing
                  : 0,
                display: "flex",
                minWidth: 0,
                position: "relative",
              }}
            >
              {whiteboardStarted && (
                <Box
                  style={{
                    position: "absolute",
                    top: typeof whiteboardSpacing === 'number' ? `${whiteboardSpacing}px` : whiteboardSpacing,
                    right: typeof whiteboardSpacing === 'number' ? `${whiteboardSpacing}px` : whiteboardSpacing,
                    zIndex: 10000,
                    pointerEvents: "auto",
                  }}
                >
                  <IconButton
                    onClick={() => {
                      mMeeting.meeting?.stopWhiteboard?.();
                    }}
                    style={{
                      backgroundColor: theme.palette.lightTheme.three,
                      padding: "0.74vh",
                      margin: 0,
                    }}
                  >
                    <CloseIcon
                      style={{
                        width: "1.85vh",
                        height: "1.85vh",
                        color:
                          appTheme === "LIGHT" || appTheme === "DARK"
                            ? "#95959E"
                            : theme.palette.common.black,
                      }}
                    />
                  </IconButton>
                </Box>
              )}
              <div
                style={{
                  width: innerWidth,
                  transition: `width ${getAnimationDuration(animationsEnabled)}ms`,
                  transitionTimingFunction: "ease-in-out",
                  borderRadius: theme.spacing(1),
                  overflow: "hidden",
                  position: "relative",
                  flex: '1 1 0px',
                  display: 'flex',
                  alignItems: spotlightMediaType === "WHITEBOARD" ? 'flex-start' : 'center',
                  justifyContent: 'flex-start',
                }}
              >
                {renderSpotlightContent()}
              </div>
            </div>

            {
              singleRow.length === 0 ||
                (mobilePortrait && presenterId) ? null : mobilePortrait &&
                  meetingLayout === meetingLayouts.SIDEBAR ? (
                <div
                  style={{
                    height: "50%",
                    width: "100%",
                    display: "flex",
                    position: "relative",
                  }}
                >
                  {singleRow.map(
                    ({
                      participantId,
                      relativeHeight,
                      relativeWidth,
                      relativeTop,
                      relativeLeft,
                    }) => {
                      return (
                        <div
                          style={{
                            padding: 8,
                            position: "absolute",
                            top: `${relativeTop}%`,
                            left: `${relativeLeft}%`,
                            width: `${relativeWidth}%`,
                            height: `${relativeHeight}%`,
                          }}
                          id={`pinned_sidebar_${participantId}`}
                          key={`pinned_sidebar_${participantId}`}
                        >
                          <div
                            style={{
                              height: `100%`,
                              width: `100%`,
                            }}
                          >
                            <ParticipantViewer
                              participantId={participantId}
                              quality={"low"}
                            />
                          </div>
                        </div>
                      );
                    }
                  )}
                </div>
              ) : (
                <div
                  style={{
                    // backgroundColor:
                    //   appTheme === appThemes.DARK
                    //     ? theme.palette.darkTheme.main
                    //     : appTheme === appThemes.LIGHT
                    //       ? theme.palette.lightTheme.main
                    //       : theme.palette.background.default,
                    // background: 'red',
                    // overflowX: "hidden",
                    overflowY: spotlightParticipantId ? "scroll" : "hidden",
                    width: spotlightParticipantId && sideBarPinnedParticipantIds?.length > 0 ? undefined : '100%',
                    flex: sidebarContainerFlex,
                    height: '100%',
                    // margin: spacing,
                    transition: `all ${800 * (animationsEnabled ? 1 : 0.5)}ms`,
                    transitionTimingFunction: "ease-in-out",
                    // paddingLeft: mainContainerHorizontalPadding,
                    // paddingRight: mainContainerHorizontalPadding,
                    // paddingTop:
                    //   singleRow.length === 2 && !spotlightParticipantId && !isMobile
                    //     ? gridVerticalSpacing
                    //     : 0,
                    // paddingTop: '1%',
                    // marginRight: guest ? '0px': '80px'
                    // padding: meetingOverlay && meetingLayout === meetingLayouts.SIDEBAR ? '4% 0%': '0%'
                    minWidth: 0,
                  }}
                >
                  <div
                    style={{
                      // height: meetingLayout === meetingLayouts.SIDEBAR ?
                      //   (spotlightParticipantId
                      //     ? (singleRow.length * presentingSideBarWidth *2) / 4
                      //     : height - 2 * spacing) -
                      //   (singleRow.length === 2 && !spotlightParticipantId && !isMobile
                      //     ? 2 * gridVerticalSpacing
                      //     : 0) : '100%',
                      // height: '100%',
                      height: meetingLayout === meetingLayouts.SIDEBAR ?
                        (spotlightParticipantId
                          ? (meetingOverlay ? (singleRow.length * presentingSideBarWidth * 2) / 4 : (singleRow.length * presentingSideBarWidth * 2) / 3)
                          : height - 2 * spacing) -
                        (singleRow.length === 2 && !spotlightParticipantId && !isMobile
                          ? 2 * gridVerticalSpacing
                          : 0) : '100%',
                      position: "relative",
                      transition: `height ${800 * (animationsEnabled ? 1 : 0.5)}ms`,
                      transitionTimingFunction: "ease-in-out",
                      zIndex: meetingOverlay || transcriptionState == "TRANSCRIPTION_STARTED" ? "900" : "1000",
                      
                    }}
                  >
                    {((meetingLayout === meetingLayouts.SIDEBAR && meetingOverlay) ? singleRow.slice(0, 4) : singleRow).map((c) => (
                      // {( singleRow).map((c) => (
                      <MemoizedMotionParticipant
                        {...c}
                        quality={
                          meetingResolution
                            ? meetingResolution === meetingResolutions.SD
                              ? "s1t2"
                              : meetingResolution === meetingResolutions.HD && "s2t2"
                            : calcQuality(singleRow?.length)
                        }
                        key={`pinned_${c.participantId}`}
                        gutter={gutter}
                        useVisibilitySensor={presenterId ? true : false}
                      />
                    ))}
                  </div>
                </div>
              )
            }
          </>
        )}
      </div>
    </>
    // </div >
  );
};

export default React.memo(
  PinnedLayoutViewContainer,
  (prevProps, nextProps) =>
    prevProps.width === nextProps.width && prevProps.height === nextProps.height
);
